/**
 * MultipleChoiceSetForm
 * Type-specific prompt form for the Multiple Choice Set AI content type
 * (QT_MULTIPLE_CHOICE_SET_BASE). Renders media selection, num_questions,
 * num_options, num_correct_min/max, and a shared PromptDescriptionInput
 * with config-driven source-type buttons. Quick Create dialog is managed
 * by PromptFormRenderer; this form auto-applies template defaults when
 * questionType changes.
 */

import './MultipleChoiceSetForm.css';

import * as React from 'react';
import find from 'lodash/find';
import {
    cn,
    Button,
    Input,
    Label,
    RadioGroup,
    RadioGroupItem,
    Card,
    CardContent,
} from '@ai-content-hub/content-kit';
import { mediaOptionLabel, usePromptTranslations, getPromptLocale } from '../i18n';
import { getFormTypeDisplayName } from '../questionTypeLabels';
import { Sparkles, Loader2, RefreshCw } from 'lucide-react';
import type { PromptFormProps } from '../types';
import type { UploadedDocument } from '../types';
import { buildDocumentSubmitFields } from '../documentSubmit';
import { PromptDescriptionInput, JobDetailsBar } from '../components';
import {
    MEDIA_OPTIONS,
    DEFAULT_FORM_STATE,
    MIN_NUM_QUESTIONS,
    MAX_NUM_QUESTIONS,
    MIN_NUM_OPTIONS,
    MAX_NUM_OPTIONS,
    type MCQSetMediaType,
    type MultipleChoiceSetFormState,
} from './types';
import { QUESTION_TYPE_PRESETS, QUICK_CREATE_TEMPLATES } from './config';

const DESCRIPTION_MAX_LENGTH = 500;

function clampNumQuestions(raw: number): number {
    return Math.min(MAX_NUM_QUESTIONS, Math.max(MIN_NUM_QUESTIONS, raw));
}

function clampNumOptions(raw: number): number {
    return Math.min(MAX_NUM_OPTIONS, Math.max(MIN_NUM_OPTIONS, raw));
}

export const MultipleChoiceSetForm: React.FC<PromptFormProps> = ({
    questionType,
    questionTypeOptions,
    allQuestionTypeOptions,
    onSubmit,
    onCancel,
    loading = false,
    disabled = false,
    isRegeneration = false,
    className,
    apiConfig,
    jobStatus,
    onRetry,
    onDismiss,
}) => {
    // EAA-2031: every label is translated (vi/en); none of this chrome is hard-coded English.
    const pt = usePromptTranslations();
    const [formState, setFormState] =
        React.useState<MultipleChoiceSetFormState>(DEFAULT_FORM_STATE);
    const touchedFieldsRef = React.useRef<Set<keyof MultipleChoiceSetFormState>>(new Set());

    const isDisabled = disabled || loading;
    const hasPrompt = formState.description.trim().length > 0;
    const hasDocuments = formState.documents.length > 0;
    const isFailed = jobStatus?.status === 'failed';
    const canSubmit = (hasPrompt || hasDocuments) && !isDisabled;
    const showRetry = isFailed && !!onRetry;

    const aiContentType = React.useMemo(() => {
        const list = allQuestionTypeOptions ?? questionTypeOptions ?? [];
        return find(list, (o) => o.slug === questionType) ?? null;
    }, [allQuestionTypeOptions, questionTypeOptions, questionType]);

    // EAA-2031: localized catalog label instead of the raw slug when no content-type row is passed.
    const typeDisplayName = getFormTypeDisplayName(questionType, aiContentType?.name, getPromptLocale());
    const typeDescription = aiContentType?.description;

    const presetButtons = React.useMemo(
        () => QUESTION_TYPE_PRESETS[questionType] ?? [],
        [questionType]
    );

    const updateField = React.useCallback(
        <K extends keyof MultipleChoiceSetFormState>(
            key: K,
            value: MultipleChoiceSetFormState[K]
        ) => {
            touchedFieldsRef.current.add(key);
            setFormState((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    const handleNumOptionsChange = React.useCallback((value: number) => {
        touchedFieldsRef.current.add('num_options');
        setFormState((prev) => ({
            ...prev,
            num_options: clampNumOptions(value),
            // WHY: Dropdown AI QT_MULTIPLE_CHOICE_SET_BASE uses scq leaf —
            // Agent ScqQuestionTypeConfig requires min=max=1. Keep locked.
            num_correct_min: 1,
            num_correct_max: 1,
        }));
    }, []);

    const handleSubmit = React.useCallback(() => {
        if (!canSubmit && !showRetry) return;
        if (showRetry && onRetry) {
            onRetry();
            return;
        }
        const prompt =
            formState.description.trim() || formState.documents.map((d) => d.name).join(', ');
        const docFields = buildDocumentSubmitFields(formState.documents);
        onSubmit({
            prompt,
            metadata: {
                source_type: formState.sourceType,
                ...docFields.metadata,
            },
            sourceDocument: docFields.sourceDocument,
            // WHY: Agent recipe keys — num_options / num_correct_min|max.
            // Deprecated Hub aliases: num_choices, num_correct, numberOfSubQuestions
            // (GEN_QUESTION_CONTRACT_SSOT §3.3). SCQ-locked: min=max=1.
            option: {
                media: formState.media,
                num_questions: formState.num_questions,
                num_options: formState.num_options,
                num_correct_min: 1,
                num_correct_max: 1,
            },
        });
    }, [canSubmit, showRetry, onRetry, formState, onSubmit]);

    const handleDescriptionChange = React.useCallback(
        (value: string) => {
            updateField('description', value);
        },
        [updateField]
    );

    const handleSourceTypeChange = React.useCallback(
        (type: string) => {
            updateField('sourceType', type as MultipleChoiceSetFormState['sourceType']);
        },
        [updateField]
    );

    const handleDocumentsChange = React.useCallback((docs: UploadedDocument[]) => {
        touchedFieldsRef.current.add('documents');
        setFormState((prev) => ({ ...prev, documents: docs }));
    }, []);

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && (canSubmit || showRetry)) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [canSubmit, showRetry, handleSubmit]
    );

    React.useEffect(() => {
        const template = find(QUICK_CREATE_TEMPLATES, (t) => t.slug === questionType);
        if (!template) return;
        setFormState((prev) => {
            const next = { ...prev };
            const touched = touchedFieldsRef.current;
            for (const [key, val] of Object.entries(template.defaults)) {
                const k = key as keyof MultipleChoiceSetFormState;
                if (!touched.has(k)) {
                    (next as Record<string, unknown>)[k] = val;
                }
            }
            return next;
        });
    }, [questionType]);

    return (
        <div className={cn('mcqs-form w-full min-h-0', className)}>
            <div className="mcqs-form-panel overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow duration-200 hover:shadow-md">
                {(typeDisplayName || typeDescription) && (
                    <div className="mcqs-form-type-header border-b border-border/70 bg-muted/25 px-5 py-4 sm:px-6 sm:py-5">
                        <div className="flex gap-3 sm:gap-4">
                            <div
                                className="mcqs-form-type-icon mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15"
                                aria-hidden
                            >
                                <Sparkles className="h-4 w-4" strokeWidth={2} />
                            </div>
                            <div className="min-w-0 flex-1 space-y-1.5">
                                {typeDisplayName ? (
                                    <h2 className="mcqs-form-type-name text-base font-semibold leading-snug tracking-tight text-foreground sm:text-lg">
                                        {typeDisplayName}
                                    </h2>
                                ) : null}
                                {typeDescription ? (
                                    <p className="mcqs-form-type-description text-sm leading-relaxed text-muted-foreground sm:max-w-3xl">
                                        {typeDescription}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mcqs-form-section-question border-b border-border/60 sm:px-5">
                    <Card className="mcqs-form-setup border-0 bg-muted/15 shadow-none py-0">
                        <CardContent className="flex flex-col gap-4 p-4 sm:p-5">
                            <div className="flex items-center justify-between">
                                <h3 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <Sparkles className="h-4 w-4" />
                                    </span>
                                    {pt.questionSetup}
                                </h3>
                            </div>

                            <div className="flex min-w-0 flex-col gap-2.5">
                                <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                                    {pt.media}
                                </Label>
                                {/*
                                    EAA-1739: each option is its own bordered/tinted
                                    chip so the selected state reads clearly at a
                                    glance (colored border + background tint), instead
                                    of the previous segmented-control's subtle
                                    bg-background-vs-bg-muted contrast.
                                */}
                                <RadioGroup
                                    value={formState.media}
                                    onValueChange={(v) =>
                                        updateField('media', v as MCQSetMediaType)
                                    }
                                    disabled={isDisabled}
                                    className="flex w-full flex-wrap gap-2"
                                >
                                    {MEDIA_OPTIONS.map((opt) => (
                                        <label
                                            key={opt.value}
                                            className={cn(
                                                'mcqs-form-media-option flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors duration-200',
                                                formState.media === opt.value
                                                    ? 'border-primary bg-primary/10 text-primary shadow-sm'
                                                    : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                                                isDisabled && 'cursor-not-allowed opacity-50'
                                            )}
                                        >
                                            <RadioGroupItem
                                                value={opt.value}
                                                className="h-4 w-4 shrink-0 border-2"
                                            />
                                            {mediaOptionLabel(opt.value, pt)}
                                        </label>
                                    ))}
                                </RadioGroup>
                            </div>

                            {/*
                                EAA-1739: same container-query approach — collapses
                                from 2×2 to stacked columns when the panel is narrow.
                            */}
                            <div className="mcqs-form-counts-grid">
                                <div className="flex min-w-0 flex-col gap-2">
                                    <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                                        {pt.numQuestions}{' '}
                                        <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        type="number"
                                        min={MIN_NUM_QUESTIONS}
                                        max={MAX_NUM_QUESTIONS}
                                        value={formState.num_questions}
                                        onChange={(e) =>
                                            updateField(
                                                'num_questions',
                                                clampNumQuestions(
                                                    parseInt(e.target.value, 10) ||
                                                        MIN_NUM_QUESTIONS
                                                )
                                            )
                                        }
                                        disabled={isDisabled}
                                        className="h-10 rounded-md"
                                    />
                                </div>
                                <div className="flex min-w-0 flex-col gap-2">
                                    <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                                        {pt.optionsPerQuestion}{' '}
                                        <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        type="number"
                                        min={MIN_NUM_OPTIONS}
                                        max={MAX_NUM_OPTIONS}
                                        value={formState.num_options}
                                        onChange={(e) =>
                                            handleNumOptionsChange(
                                                parseInt(e.target.value, 10) || MIN_NUM_OPTIONS
                                            )
                                        }
                                        disabled={isDisabled}
                                        className="h-10 rounded-md"
                                    />
                                </div>
                                <div className="flex min-w-0 flex-col gap-2">
                                    <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                                        {pt.correctPerQuestion}
                                    </Label>
                                    <Input
                                        type="number"
                                        value={1}
                                        disabled
                                        className="h-10 rounded-md"
                                        aria-label={pt.singleChoiceOneCorrect}
                                    />
                                    <p className="mcqs-form-single-hint text-xs text-muted-foreground">
                                        {pt.singleChoiceOneCorrect}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mcqs-form-section-description sm:px-5">
                    <PromptDescriptionInput
                        value={formState.description}
                        onChange={handleDescriptionChange}
                        onKeyDown={handleKeyDown}
                        activePresetKey={formState.sourceType}
                        onActivePresetKeyChange={handleSourceTypeChange}
                        presetButtons={presetButtons}
                        documents={formState.documents}
                        onDocumentsChange={handleDocumentsChange}
                        apiConfig={apiConfig}
                        disabled={isDisabled}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        className="mcqs-form-description rounded-none border-0 bg-transparent shadow-none ring-0"
                    />
                </div>

                <div className="mcqs-form-actions flex flex-col gap-3 border-t border-border/80 bg-muted/15 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5">
                    <div className="mcqs-form-footer-status flex min-w-0 flex-1 items-center justify-start">
                        <JobDetailsBar
                            jobStatus={jobStatus ?? null}
                            onDismiss={onDismiss}
                            className="mcqs-form-job-details"
                        />
                    </div>
                    <div className="mcqs-form-footer-actions flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
                        {onCancel && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onCancel}
                                disabled={isDisabled}
                                className="min-h-11 min-w-[88px] px-5 transition-colors duration-200"
                            >
                                {pt.cancel}
                            </Button>
                        )}
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleSubmit}
                            disabled={!canSubmit && !showRetry}
                            className="mcqs-form-submit min-h-11 gap-2 px-6 font-medium shadow-sm transition-colors duration-200 hover:opacity-[0.97] disabled:opacity-60"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : showRetry ? (
                                <RefreshCw className="h-4 w-4" />
                            ) : isRegeneration ? (
                                <RefreshCw className="h-4 w-4" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            {loading
                                ? pt.creating
                                : showRetry
                                  ? pt.retry
                                  : isRegeneration
                                    ? pt.regenerate
                                    : pt.createQuestions}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultipleChoiceSetForm;
